'use client';

import { useRouter } from 'next/navigation';
import { useUiPreferences } from '@/app/Components/UiPreferencesProvider';
import {
  buildAcademicBadgeStyle,
  getAcademicSwatch,
} from '@/app/lib/academicPalette';

type AcademicProgramBadgeProps = {
  name: string;
  degreeCode?: string | null;
  startTerm?: string | null;
  className?: string;
  compact?: boolean;
  allowWrap?: boolean;
  /** Keep the full source name for tooltips even when displayName is cleaned. */
  titleName?: string;
  onActivate?: () => void;
  interactive?: boolean;
  /** A context that already colors programs (the degree planner) passes its color so the badge matches. */
  accentColor?: string;
};

export function normalizeProgramDisplayName(name: string): string {
  const cleaned = name
    .replace(/^Degree\s+Audit\s+for\s+/i, '')
    .replace(/\s+/g, ' ')
    .trim();
  return cleaned || name;
}

export default function AcademicProgramBadge({
  name,
  degreeCode,
  startTerm,
  className = '',
  compact = false,
  allowWrap = false,
  titleName,
  onActivate,
  interactive = true,
  accentColor,
}: AcademicProgramBadgeProps) {
  const router = useRouter();
  const { academicPalettePreferences, academicPalettePresets } = useUiPreferences();
  const displayName = normalizeProgramDisplayName(name);
  const swatch = getAcademicSwatch(
    `${degreeCode ?? ''}:${displayName}`,
    academicPalettePreferences.programPaletteId,
    'program',
    academicPalettePresets
  );
  const badgeStyle = accentColor
    ? { backgroundColor: `color-mix(in srgb, ${accentColor} 12%, var(--ss-surface))`, borderColor: `color-mix(in srgb, ${accentColor} 40%, var(--ss-border))`, color: 'var(--ss-text)' }
    : { ...buildAcademicBadgeStyle(swatch), color: 'var(--ss-text)' };
  const dotColor = accentColor ?? swatch.solid;
  const tooltip = [titleName ?? name, degreeCode, startTerm].filter(Boolean).join(' • ');
  const content = (
    <>
      <span className="inline-flex h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: dotColor }} aria-hidden="true" />
      <span className={allowWrap ? 'min-w-0 flex-1 whitespace-normal text-left [overflow-wrap:break-word] line-clamp-2' : 'min-w-0 flex-1 truncate text-left'}>{displayName}</span>
      {degreeCode ? <span className={allowWrap ? 'shrink-0 break-words whitespace-normal opacity-80' : 'max-w-[40%] shrink-0 truncate opacity-80'}>{degreeCode}</span> : null}
      {startTerm ? <span className={allowWrap ? 'shrink-0 break-words whitespace-normal opacity-70' : 'max-w-[30%] shrink-0 truncate opacity-70'}>{startTerm}</span> : null}
    </>
  );
  // A pill only reads as a pill on one line. When the label is allowed to wrap, a
  // narrow column squeezes it to one word per line and `rounded-full` inflates the
  // result into a circle, so a wrapping badge becomes a rounded rectangle and keeps
  // enough width that words stay together.
  const sharedClassName = `inline-flex max-w-full min-w-0 items-start gap-2 border ${allowWrap ? 'min-w-[10rem] items-center rounded-card' : 'items-center rounded-full'} ${compact ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm'} font-semibold ${className}`;

  if (!interactive) {
    return (
      <span className={sharedClassName} style={badgeStyle} title={tooltip}>
        {content}
      </span>
    );
  }

  return (
    <button
      type="button"
      className={`${sharedClassName} text-left transition hover:brightness-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]`}
      style={badgeStyle}
      title={tooltip}
      aria-label={`Open details for ${displayName}`}
      onClick={(event) => {
        event.stopPropagation();
        if (onActivate) {
          onActivate();
          return;
        }
        router.push(`/degree-search?q=${encodeURIComponent(displayName)}`);
      }}
    >
      {content}
    </button>
  );
}
